import type { ReactElement } from 'react';
import { useState } from 'react';

import { ErrorMessage, Route, ApiRoute, FeatureFlag } from 'config';
import { post } from 'utils/network';
import useCurrentUser from 'hooks/useCurrentUser';

import { Component as InstagramIcon } from 'icons/icon-social--instagram.svg';
import { Component as FacebookIcon } from 'icons/icon-social--facebook.svg';
import { Component as PinterestIcon } from 'icons/icon-social--pinterest.svg';
import { Component as TwitterIcon } from 'icons/icon-social--twitter.svg';
import { Component as RightArrowIcon } from 'icons/icon-ui--arrow-right-outline.svg';

import AppStoreLink from 'components/shared/AppStoreLink';
import Link from './Link';
import SocialButton from './SocialButton';

const emailRegexp = /[\w+-.]+@[a-z\d-]+(\.[a-z\d-]+)*\.[a-z]+/i;

function Footer(): ReactElement {
  const [email, setEmail] = useState('');
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { currentUser } = useCurrentUser();
  const hasFozenMealsEnabled = currentUser.hasFeatureEnabled(
    FeatureFlag.FROZEN_MEALS,
  );

  const careerURL = window.gon?.careersURL;
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setIsEmailInvalid(true);
      return;
    }

    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith('do_not_sell='))
      ?.split('=')[1];

    const response = await post(ApiRoute.WEEKLY_NEWS, {
      email,
      do_not_sell: cookieValue || false,
    });

    if (!emailRegexp.test(email)) {
      setIsEmailInvalid(true);
    } else if (response.status === 201) {
      setSuccess(true);
      setErrorMessage('');
      setIsEmailInvalid(false);
    } else if (response.status === 422) {
      setErrorMessage(ErrorMessage.ALREADY_SUBSCRIBED);
      setSuccess(false);
      setIsEmailInvalid(false);
    }
  };

  const hasSubscribed = errorMessage !== '' && !isEmailInvalid;
  const hasSucceeded = isSuccess && !isEmailInvalid;

  return (
    <footer role="contentinfo" className="d-mapp-none d-iframe-none">
      <div className="c-footer">
        <div className="l-container">
          <div className="row">
            <div className="col-12 col-xs-10 offset-xs-1 col-md-8 offset-md-0">
              <nav className="c-footer__nav row" aria-label="Footer navigation">
                <ul className="col-4">
                  <li>
                    <span className="c-footer__title">
                      Our Product
                    </span>
                  </li>
                  <li>
                    <Link href="/weekly-menu/meal-kits">
                      Meal Kits
                    </Link>
                  </li>
                  <li>
                    <Link href={Route.WEEKLY_MENU_READY_TO_EAT}>
                      Ready To Eat
                    </Link>
                  </li>
                  <li>
                    <Link href={Route.GROCERY_MEALS}>
                      Grocery
                    </Link>
                  </li>

                  {hasFozenMealsEnabled && (
                    <li>
                      <Link href={Route.FROZEN_MEALS}>
                        Frozen Meals
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link href="/why-plants">
                      Why Plants
                    </Link>
                  </li>
                  <li>
                    <Link href="/plant-based-recipes">
                      All Recipes
                    </Link>
                  </li>
                </ul>

                <ul className="col-4">
                  <li>
                    <span className="c-footer__title">
                      Help
                    </span>
                  </li>
                  <li>
                    <Link href="/recycle">
                      Recycle
                    </Link>
                  </li>
                  <li>
                    <Link href="https://purplecarrot.zendesk.com/hc/en-us">
                      <abbr>
                        FAQs
                      </abbr>
                    </Link>
                  </li>
                  <li>
                    <Link href="/contacts/new">
                      Contact Us
                    </Link>
                  </li>
                </ul>

                <ul className="col-4">
                  <li>
                    <span className="c-footer__title">
                      Connect
                    </span>
                  </li>
                  <li>
                    <Link href="/refer">
                      Refer and Earn
                    </Link>
                  </li>
                  <li>
                    <Link href="/blog">
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link href="mailto:partnerships@purplecarrot.com">
                      Partnerships
                    </Link>
                  </li>
                  <li>
                    <Link href="https://app.impact.com/campaign-mediapartner-signup/Purple-Carrot.brand?execution=e1s1">
                      Affiliate Program
                    </Link>
                  </li>
                  <li>
                    <Link href={careerURL}>
                      Careers
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

            <hr className="col-12 col-xs-10 offset-xs-1 d-md-none" />

            <div className="col-12 col-xs-10 offset-xs-1 col-md-4 offset-md-0">

              <div className="c-footer-social">
                <p>Follow us on:</p>
                <ul className="c-social-buttons">
                  <SocialButton
                    title="Instagram"
                    href="http://instagram.com/purplecarrotxo"
                  >
                    <InstagramIcon />
                  </SocialButton>

                  <SocialButton
                    title="Facebook"
                    href="https://www.facebook.com/purplecarrotxo"
                  >
                    <FacebookIcon />
                  </SocialButton>

                  <SocialButton
                    title="Pinterest"
                    href="https://pinterest.com/thepurplecarrot"
                  >
                    <PinterestIcon />
                  </SocialButton>

                  <SocialButton
                    title="Twitter"
                    href="https://www.twitter.com/purplecarrotxo"
                  >
                    <TwitterIcon />
                  </SocialButton>
                </ul>
              </div>

              <div className="c-footer-form">
                <form
                  className="simple_form new_weekly_news_form"
                  onSubmit={handleSubmit}
                >
                  <div
                    className={[
                      'c-form-group',
                      'email',
                      'required',
                      'weekly_news_form_email',
                      isEmailInvalid || hasSubscribed ? 'is-invalid' : '',
                    ].join(' ')}
                  >
                    <label className="c-footer-form__title" htmlFor="email_for_weekly_menu">
                      Sign-Up for Our Weekly Menu:
                    </label>

                    <div className="c-input-group c-input-group--button">
                      <input
                        id="email_for_weekly_menu"
                        className="string email required"
                        placeholder="@mail"
                        type="email"
                        onChange={(e) => setEmail(e.target.value)}
                      />

                      <button
                        name="button"
                        type="submit"
                        className="c-button c-button--primary-light"
                        aria-label="Subscribe for Weekly Menu"
                      >
                        <RightArrowIcon />
                      </button>
                    </div>
                    {isEmailInvalid && (
                      <div className="c-form-group__invalid-text">
                        {ErrorMessage.INVALID_EMAIL}
                      </div>
                    )}
                    {
                      hasSubscribed && (
                        <div className="c-form-group__invalid-message">
                          {errorMessage}
                        </div>
                      )
                    }
                    {
                      hasSucceeded && (
                        <div className="c-form-group__success-text">
                          Success!
                        </div>
                      )
                    }
                  </div>
                </form>
              </div>
              <div className="c-footer-app">
                <p>
                  Download the
                  {' '}
                  <abbr>App</abbr>
                </p>
                <AppStoreLink />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="c-footer-copyright u-text--center">
        <div className="l-container">
          <div className="c-footer-copyright__links_group">
            <a href="/terms">Terms &amp; Conditions</a>
            &nbsp;|
            <a href="/privacy">Privacy Policy</a>
            &nbsp;|
            <a href="/california_privacy">California Privacy Policy</a>
            &nbsp;|
            <a href="/do_not_sell">Do Not Sell My Info</a>
            &nbsp;|
            <a href="/accessibility">Accessibility Statement</a>
            &nbsp;|
            <a href="/sitemap">Site Map</a>
          </div>
          <p>
            © Copyright 2022 Purple Carrot. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
